import React, { useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { Globe, X, Check, Plus, Zap, Square, Layers, Image, Rocket } from 'lucide-react'
import LoadingSpinner from '../common/LoadingSpinner'

const TEMPLATES = [
  { id: 'blank', name: 'Blank', Icon: Square, desc: 'Start from an empty canvas' },
  { id: 'restaurant', name: 'Restaurant', Icon: Globe, desc: 'Menu, hours, reservations' },
  { id: 'portfolio', name: 'Portfolio', Icon: Image, desc: 'Showcase work in a gallery' },
  { id: 'business', name: 'Business', Icon: Layers, desc: 'Services, team & contact' },
  { id: 'landing', name: 'Landing page', Icon: Rocket, desc: 'One-page conversion focus' },
]

function Field({ label, hint, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-soft)' }}>
        {label}{hint && <span style={{ color: 'var(--text-faint)', fontWeight: 400 }}> · {hint}</span>}
      </label>
      {children}
    </div>
  )
}

// Client-facing "request new site" modal — mirrors the admin NewSite modal.
export default function NewSiteModal({ onClose, onCreated }) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [selectedTemplate, setSelectedTemplate] = useState('blank')
  const [nameError, setNameError] = useState('')

  const finish = () => {
    toast.success('Site created successfully!')
    queryClient.invalidateQueries('client-sites')
    queryClient.invalidateQueries('sites')
    onCreated && onCreated()
    onClose()
  }

  const createSiteMutation = useMutation(
    (siteData) => apiService.post('/sites', siteData),
    {
      onSuccess: finish,
      onError: (error) => {
        // Some environments surface a created (201) response as an error
        if (error.response?.status === 201 && error.response?.data?.id) { finish(); return }
        toast.error(error.response?.data?.error || error.response?.data?.errors?.[0]?.msg || 'Failed to create site')
      },
    }
  )

  const submit = () => {
    if (!name.trim()) { setNameError('Site name is required'); return }
    createSiteMutation.mutate({ name: name.trim(), template_id: null, description: description.trim() })
  }

  return (
    <div className="drawer-backdrop" style={{ justifyContent: 'center', alignItems: 'center' }} onClick={onClose}>
      <div className="card" style={{ width: 660, maxWidth: '94vw', boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className="icon-tile" style={{ width: 36, height: 36 }}><Globe style={{ width: 16, height: 16 }} /></span>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-0.01em', margin: 0 }}>Request a new site</h2>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>Pick a starting template and we'll get it set up.</div>
          </div>
          <button className="site-action" onClick={onClose}><X style={{ width: 15, height: 15 }} /></button>
        </div>

        {/* Body */}
        <div style={{ padding: 22, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Field label="Site name">
            <input
              className="input"
              autoFocus
              placeholder="e.g. Winter Pop-up 2026"
              value={name}
              onChange={e => { setName(e.target.value); if (nameError) setNameError('') }}
              style={nameError ? { borderColor: 'var(--danger)' } : {}}
            />
            {nameError && <span style={{ color: 'var(--danger)', fontSize: 11, marginTop: 2 }}>{nameError}</span>}
          </Field>

          <Field label="Template">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(118px, 1fr))', gap: 10 }}>
              {TEMPLATES.map(t => {
                const sel = selectedTemplate === t.id
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setSelectedTemplate(t.id)}
                    style={{
                      textAlign: 'left', padding: 13, borderRadius: 12,
                      border: `1.5px solid ${sel ? 'var(--border-accent)' : 'var(--border)'}`,
                      background: sel ? 'var(--accent-soft)' : 'var(--surface)',
                      transition: 'all 0.14s ease', position: 'relative',
                    }}
                  >
                    <span className="icon-tile" style={{ width: 30, height: 30, fontSize: 14, background: sel ? 'var(--accent)' : 'var(--surface-2)', color: sel ? 'white' : 'var(--text-muted)', border: 'none' }}>
                      <t.Icon style={{ width: 14, height: 14 }} />
                    </span>
                    <div style={{ fontSize: 12.5, fontWeight: 600, marginTop: 9 }}>{t.name}</div>
                    <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 3, lineHeight: 1.35 }}>{t.desc}</div>
                    {sel && <span style={{ position: 'absolute', top: 10, right: 10, color: 'var(--accent-3)' }}><Check style={{ width: 13, height: 13 }} /></span>}
                  </button>
                )
              })}
            </div>
          </Field>

          <Field label="Description" hint="optional">
            <textarea className="input" rows={3} style={{ resize: 'vertical', lineHeight: 1.6 }} placeholder="What is this site for?" value={description} onChange={e => setDescription(e.target.value)} />
          </Field>
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 22px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 11.5, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Zap style={{ width: 12, height: 12, color: 'var(--accent-3)' }} /> Starts as a draft you can edit right away
          </span>
          <button type="button" className="btn" style={{ marginLeft: 'auto' }} onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={submit} disabled={createSiteMutation.isLoading}>
            {createSiteMutation.isLoading ? <LoadingSpinner size="sm" /> : <><Plus style={{ width: 14, height: 14 }} /> Create site</>}
          </button>
        </div>
      </div>
    </div>
  )
}
